'use client';

import { useCallback } from 'react';
import type { ChatMessage } from '@/types';

const KEY    = 'zain_session';
const TTL_MS = 24 * 60 * 60 * 1000; // 24 h — chat history expires; profile persists

export interface VisitorProfile {
  sessionId:           string | null;
  messages:            ChatMessage[];
  sessionCount:        number;
  firstVisit:          string;
  lastVisit:           string;
  source:              string | null;
  hasSubmittedInquiry: boolean;
  name:                string | null;
  rated:               boolean;
  _savedAt:            number;
}

export function detectSource(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const p = new URLSearchParams(window.location.search);
    const u = p.get('utm_source');
    if (u) return u.toLowerCase();
    const r = document.referrer.toLowerCase();
    if (r.includes('linkedin'))                        return 'linkedin';
    if (r.includes('github'))                          return 'github';
    if (r.includes('google'))                          return 'google';
    if (r.includes('twitter') || r.includes('x.com')) return 'twitter';
    if (r.includes('facebook') || r.includes('fb'))   return 'facebook';
    if (r.includes('upwork'))                          return 'upwork';
    if (r.includes('freelancer'))                      return 'freelancer';
  } catch { /**/ }
  return null;
}

const blank = (): VisitorProfile => ({
  sessionId:           null,
  messages:            [],
  sessionCount:        0,
  firstVisit:          new Date().toISOString(),
  lastVisit:           new Date().toISOString(),
  source:              null,
  hasSubmittedInquiry: false,
  name:                null,
  rated:               false,
  _savedAt:            0,
});

export function useVisitorMemory() {
  const read = useCallback((): VisitorProfile => {
    if (typeof window === 'undefined') return blank();
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return blank();
      const data: VisitorProfile = JSON.parse(raw);
      // Chat history expires after 24 h; keep profile intact
      if (data._savedAt && Date.now() - data._savedAt > TTL_MS) {
        return { ...data, sessionId: null, messages: [], rated: false };
      }
      return data;
    } catch {
      return blank();
    }
  }, []);

  const write = useCallback((patch: Partial<VisitorProfile>) => {
    if (typeof window === 'undefined') return;
    try {
      const current = read();
      localStorage.setItem(KEY, JSON.stringify({ ...current, ...patch, _savedAt: Date.now() }));
    } catch { /**/ }
  }, [read]);

  return { read, write };
}
